"use client";

import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
} from "@/hooks/notification-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, Check, CheckCheck, Loader2, Sparkles } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";

const API = process.env.NEXT_PUBLIC_API_URI ?? "http://localhost:8000";

function timeAgo(iso: string, isAr: boolean) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (isAr) {
    if (m < 1) return "الآن";
    if (m < 60) return `منذ ${m} د`;
    if (h < 24) return `منذ ${h} س`;
    return `منذ ${d} ي`;
  }
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

function imgUrl(image?: string) {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  return `${API}/images/properties/${image}`;
}

export default function NotificationsList({
  setShow,
}: {
  notifications?: any[];
  setShow: any;
}) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const router = useRouter();

  const { data, isLoading } = useNotifications();
  const markAll = useMarkAllRead();
  const markOne = useMarkRead();

  const items: any[] = data?.notifications || [];
  const unread: number = data?.unread || 0;

  const openItem = (n: any, e: React.MouseEvent) => {
    if (!n.read) markOne.mutate(n._id);
    if (n.link) {
      setShow(false);
      router.push(`/${locale}${n.link}`);
    } else {
      // no destination → just mark read, keep the panel open so the change is visible
      e.stopPropagation();
    }
  };

  const markOneRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    markOne.mutate(id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 360, damping: 26 }}
      dir={isAr ? "rtl" : "ltr"}
      onClick={() => setShow(false)}
      className="w-[340px] max-w-[90vw] rounded-3xl overflow-hidden bg-white shadow-[0_25px_60px_-12px_rgba(13,53,137,0.35)] ring-1 ring-black/5"
    >
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a2a6e] via-[#0d3589] to-[#071f52] px-5 py-4">
        <div className="pointer-events-none absolute -top-10 -right-6 size-40 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-white/10 flex items-center justify-center text-amber-300">
              <BellRing size={18} />
            </div>
            <div>
              <h3 className="font-black text-white text-sm">
                {isAr ? "الإشعارات" : "Notifications"}
              </h3>
              <p className="text-[11px] text-blue-100/60">
                {unread > 0
                  ? isAr ? `${unread} غير مقروء` : `${unread} unread`
                  : isAr ? "لا جديد" : "all caught up"}
              </p>
            </div>
          </div>
          {unread > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                markAll.mutate();
              }}
              disabled={markAll.isPending}
              className="flex items-center gap-1 text-[11px] font-bold text-amber-200 hover:text-amber-100 bg-white/10 hover:bg-white/20 rounded-full px-2.5 py-1.5 transition-colors"
            >
              <CheckCheck size={13} />
              {isAr ? "تعليم الكل" : "Mark all"}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-[#0d3589]" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="size-14 rounded-2xl bg-[#0d3589]/5 flex items-center justify-center mb-3">
              <Sparkles className="text-[#0d3589]/40" size={24} />
            </div>
            <p className="text-sm font-bold text-slate-700">
              {isAr ? "لا توجد إشعارات بعد" : "No notifications yet"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {isAr
                ? "احفظ بحثك وسننبّهك بأي عقار جديد يطابقه."
                : "Save a search and we'll alert you about new matches."}
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {items.map((n) => {
              const image = imgUrl(n.image);
              return (
                <motion.div
                  key={n._id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={(e) => openItem(n, e)}
                  className={`w-full cursor-pointer text-start flex items-center gap-3 px-4 py-3.5 border-b border-slate-50 hover:bg-slate-50/80 transition-colors relative ${
                    !n.read ? "bg-[#0d3589]/[0.035]" : ""
                  }`}
                >
                  {/* unread dot */}
                  {!n.read && (
                    <span className="absolute top-1/2 -translate-y-1/2 start-1.5 size-2 rounded-full bg-amber-400 shadow-[0_0_0_3px_rgba(245,158,11,0.2)]" />
                  )}

                  <div className="relative size-14 rounded-xl overflow-hidden bg-slate-100 shrink-0 ms-1.5">
                    {image ? (
                      <Image src={image} alt="" fill className="object-cover" />
                    ) : (
                      <div className="size-full flex items-center justify-center text-[#0d3589]/40">
                        <BellRing size={20} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-slate-800 line-clamp-1">
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                        {n.body}
                      </p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {timeAgo(n.createdAt, isAr)}
                    </p>
                  </div>

                  {/* explicit "mark as read" — only for unread */}
                  {!n.read && (
                    <button
                      onClick={(e) => markOneRead(n._id, e)}
                      title={isAr ? "تعليم كمقروء" : "Mark as read"}
                      className="shrink-0 size-8 rounded-full bg-[#0d3589]/8 hover:bg-[#0d3589] text-[#0d3589] hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <Check size={15} />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
}
